import React from 'react';
import { AlertTriangle, Shield, BookOpen, Scale, FileText, Info } from 'lucide-react';
import SEO from '../components/SEO';

const Disclaimer = () => {
  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="Disclaimer & Legal Notice - Vitta Finance Academy | CIRO Exam Prep Terms"
        description="Read the legal disclaimer for Vitta Finance Academy. Important information about CIRO exam preparation materials, educational content limitations, no affiliation with CIRO/IIROC/MFDA, and liability terms. Alberta, Canada jurisdiction."
        keywords="Vitta Finance disclaimer, CIRO exam prep disclaimer, educational materials disclaimer, CIRO not affiliated, IIROC not affiliated, MFDA not affiliated, exam prep legal notice, Vitta Finance terms"
      />

      {/* Hero Section */}
      <section style={{
        padding: '100px 0 60px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Shield size={40} color="var(--gold-primary)" />
            </div>
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              marginBottom: '16px'
            }}>
              Disclaimer & Legal Notice
            </h1>
            <p style={{
              fontSize: '1.1rem',
              opacity: 0.9
            }}>
              Last Updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{
        padding: '80px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {/* Important Notice Box */}
            <div style={{
              padding: '32px',
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              border: '2px solid var(--gold-primary)',
              borderRadius: '16px',
              marginBottom: '48px',
              display: 'flex',
              gap: '20px',
              alignItems: 'flex-start'
            }}>
              <AlertTriangle size={32} color="var(--gold-primary)" style={{ flexShrink: 0, marginTop: '4px' }} />
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '12px', color: 'var(--brand-primary)' }}>
                  Important Notice
                </h2>
                <p style={{ lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                  Please read this disclaimer carefully before using any materials, courses, or services provided by Vitta Finance Academy Inc. By accessing or using our website, courses, books, or any other educational materials, you acknowledge that you have read, understood, and agree to be bound by the terms of this disclaimer.
                </p>
              </div>
            </div>

            {/* Sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
              
              {/* Educational Purpose */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BookOpen size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    1. Educational Purpose Only
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    All content, materials, courses, books, practice questions, mock exams, and other resources provided by Vitta Finance Academy Inc. ("Vitta Finance," "we," "us," or "our") are intended solely for <strong>educational and informational purposes</strong>. Our materials are designed to help candidates prepare for the Canadian Investment Regulatory Organization (CIRO) certification examinations, including but not limited to:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: '16px' }}>
                    {[
                      'Canadian Investment Regulatory Exam (CIRE)',
                      'Retail Securities Exam (RSE)',
                      'Institutional Securities Exam (ISE)',
                      'Derivatives Fundamentals and Options Licensing Course Exam'
                    ].map((item, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        marginBottom: '10px',
                        color: 'var(--text-secondary)'
                      }}>
                        <span style={{ color: 'var(--gold-primary)', fontWeight: 'bold' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    Our educational materials do not constitute professional financial, legal, investment, or tax advice. Users should not rely on our content as a substitute for professional advice from qualified practitioners.
                  </p>
                </div>
              </div>

              {/* No Guarantee */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AlertTriangle size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    2. No Guarantee of Exam Success
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    While Vitta Finance Academy strives to provide comprehensive and high-quality exam preparation materials, <strong>we do not guarantee that any user will pass their CIRO examinations</strong>. Examination success depends on numerous factors including, but not limited to:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: '16px' }}>
                    {[
                      'Individual study habits and dedication',
                      'Prior knowledge and educational background',
                      'Time devoted to preparation',
                      'Test-taking abilities and examination conditions',
                      'Changes to exam content, format, or passing criteria by CIRO'
                    ].map((item, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        marginBottom: '10px',
                        color: 'var(--text-secondary)'
                      }}>
                        <span style={{ color: 'var(--gold-primary)', fontWeight: 'bold' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    Any pass rates, success statistics, or testimonials referenced on our website or in our marketing materials are based on historical data and individual experiences, which may not be representative of future results.
                  </p>
                </div>
              </div>

              {/* Not Affiliated */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Info size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    3. No Affiliation with CIRO
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    Vitta Finance Academy Inc. is an <strong>independent educational company</strong> and is not affiliated with, endorsed by, sponsored by, or in any way officially connected with:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: '16px' }}>
                    {[
                      'Canadian Investment Regulatory Organization (CIRO)',
                      'Investment Industry Regulatory Organization of Canada (IIROC)',
                      'Mutual Fund Dealers Association of Canada (MFDA)',
                      'Any provincial securities commission or regulatory body',
                      'Any financial institution or brokerage firm'
                    ].map((item, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        marginBottom: '10px',
                        color: 'var(--text-secondary)'
                      }}>
                        <span style={{ color: 'var(--gold-primary)', fontWeight: 'bold' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    All trademarks, service marks, trade names, and logos referenced herein belong to their respective owners. Our use of these terms is solely for identification and educational purposes.
                  </p>
                </div>
              </div>

              {/* Accuracy of Information */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileText size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    4. Accuracy of Information
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    While we make every effort to ensure the accuracy, completeness, and currency of the information provided in our materials, <strong>we cannot guarantee that all information is error-free or up-to-date</strong>. Financial regulations, examination requirements, and industry standards are subject to change without notice.
                  </p>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    Users are strongly encouraged to:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {[
                      'Verify current exam requirements directly with CIRO',
                      'Consult official CIRO publications and guidelines',
                      'Stay informed about regulatory updates and changes',
                      'Seek clarification from qualified professionals when needed'
                    ].map((item, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        marginBottom: '10px',
                        color: 'var(--text-secondary)'
                      }}>
                        <span style={{ color: 'var(--gold-primary)', fontWeight: 'bold' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Limitation of Liability */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Scale size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    5. Limitation of Liability
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    To the fullest extent permitted by applicable law, Vitta Finance Academy Inc., its directors, officers, employees, instructors, affiliates, and agents shall not be liable for any:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: '16px' }}>
                    {[
                      'Direct, indirect, incidental, special, consequential, or punitive damages',
                      'Loss of profits, revenue, data, or business opportunities',
                      'Examination failures or delays in obtaining certification',
                      'Career setbacks or employment-related consequences',
                      'Errors, omissions, or inaccuracies in our materials',
                      'Technical issues, service interruptions, or access problems'
                    ].map((item, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        marginBottom: '10px',
                        color: 'var(--text-secondary)'
                      }}>
                        <span style={{ color: 'var(--gold-primary)', fontWeight: 'bold' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    This limitation applies regardless of whether the damages arise from contract, tort, negligence, strict liability, or any other legal theory, even if we have been advised of the possibility of such damages.
                  </p>
                </div>
              </div>

              {/* Intellectual Property */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Shield size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    6. Intellectual Property Rights
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    All content, materials, and resources provided by Vitta Finance Academy, including but not limited to textbooks, study guides, practice questions, mock exams, video lectures, presentations, and digital content, are protected by copyright and other intellectual property laws.
                  </p>
                  <p style={{ lineHeight: 1.8, marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    <strong>Unauthorized reproduction, distribution, modification, or commercial use of our materials is strictly prohibited</strong> and may result in legal action. Users are granted a limited, non-exclusive, non-transferable license to use our materials solely for personal educational purposes.
                  </p>
                </div>
              </div>

              {/* Governing Law */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Scale size={24} color="var(--brand-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>
                    7. Governing Law
                  </h2>
                </div>
                <div style={{ paddingLeft: '64px' }}>
                  <p style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    This disclaimer and any disputes arising from your use of our services shall be governed by and construed in accordance with the laws of the Province of Alberta, Canada, without regard to its conflict of law provisions. Any legal proceedings shall be brought exclusively in the courts of Alberta, Canada.
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div style={{
                padding: '32px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '16px', color: 'var(--brand-primary)' }}>
                  Questions About This Disclaimer?
                </h2>
                <p style={{ lineHeight: 1.8, marginBottom: '20px', color: 'var(--text-secondary)' }}>
                  If you have any questions or concerns about this disclaimer or our educational materials, please contact us:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:info@vittafinance.ca" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>
                      info@vittafinance.ca
                    </a>
                  </p>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    <strong>Company:</strong> Vitta Finance Academy Inc.
                  </p>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    <strong>Location:</strong> Alberta, Canada
                  </p>
                </div>
              </div>

              {/* Acceptance */}
              <div style={{
                padding: '32px',
                background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
                borderRadius: '16px',
                border: '1px solid var(--brand-primary)',
                textAlign: 'center'
              }}>
                <p style={{ lineHeight: 1.8, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  By using our website, purchasing our courses, or accessing any of our educational materials, you acknowledge that you have read, understood, and agree to be bound by this disclaimer in its entirety.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;
